import React from 'react';
import { X, AlertCircle, CheckCircle2, Info } from 'lucide-react';

/* ── Spinner ─────────────────────────────────────────────────── */
export default function Spinner({ fullscreen, size = 24 }) {
  const el = (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2.5px solid rgba(108,99,255,0.2)`,
      borderTopColor: 'var(--brand-500)',
      animation: 'spin 0.7s linear infinite',
    }} />
  );
  if (!fullscreen) return el;
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'rgba(255,255,255,0.8)', zIndex: 100,
    }}>
      {el}
    </div>
  );
}

/* ── Modal ───────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, subtitle, children, footer, maxWidth = 520 }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className="modal" style={{ maxWidth }}>
        <div className="modal-header">
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17, color: 'var(--text-primary)' }}>
              {title}
            </div>
            {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ── Pagination ──────────────────────────────────────────────── */
export function Pagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null;
  const from  = (page - 1) * pageSize + 1;
  const to    = Math.min(page * pageSize, total);

  const pages = [];
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        {from}–{to} of {total?.toLocaleString()}
      </span>
      <div className="pagination">
        <button className="pag-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
        {pages.map(p => (
          <button key={p} className={`pag-btn ${p === page ? 'active' : ''}`} onClick={() => onChange(p)}>{p}</button>
        ))}
        <button className="pag-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
      </div>
    </div>
  );
}

/* ── FormField ───────────────────────────────────────────────── */
export function FormField({ label, error, hint, required, children }) {
  return (
    <div className="form-group">
      {label && (
        <label className="form-label">
          {label}{required && <span style={{ color: 'var(--danger)', marginLeft: 2 }}>*</span>}
        </label>
      )}
      {children}
      {error && <div className="form-error">{error}</div>}
      {hint && !error && <div className="form-hint">{hint}</div>}
    </div>
  );
}

/* ── StatusBadge ─────────────────────────────────────────────── */
const STATUS_MAP = {
  active:         'badge-active',
  inactive:       'badge-inactive',
  terminated:     'badge-terminated',
  suspended:      'badge-terminated',
  pending:        'badge-pending',
  approved:       'badge-approved',
  rejected:       'badge-rejected',
  cancelled:      'badge-inactive',
  on_leave:       'badge-on_leave',
  draft:          'badge-draft',
  computed:       'badge-computed',
  pending_approval:'badge-pending',
  posted_to_sap:  'badge-posted_to_sap',
  present:        'badge-approved',
  absent:         'badge-rejected',
  late:           'badge-pending',
  half_day:       'badge-pending',
  open:           'badge-pending',
  under_review:   'badge-pending',
  resolved:       'badge-approved',
  closed:         'badge-inactive',
  exceeds:        'badge-approved',
  meets:          'badge-active',
  below:          'badge-rejected',
  Complete:       'badge-approved',
  Incomplete:     'badge-rejected',
  'Pending Verification': 'badge-pending',
  complete:       'badge-approved',
  incomplete:     'badge-rejected',
  pending_verification: 'badge-pending',
};

export function StatusBadge({ status }) {
  const cls = STATUS_MAP[status] || 'badge-inactive';
  const label = status?.replace(/_/g, ' ') || '—';
  return <span className={`badge ${cls}`}>{label.charAt(0).toUpperCase() + label.slice(1)}</span>;
}

/* ── EmptyState ──────────────────────────────────────────────── */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="empty">
      {Icon && <Icon size={36} />}
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-secondary)' }}>{title}</div>
      {description && <div style={{ fontSize: 13, maxWidth: 300 }}>{description}</div>}
      {action}
    </div>
  );
}

/* ── Alert ───────────────────────────────────────────────────── */
export function Alert({ type = 'info', children, action }) {
  const map = {
    info:    { cls: 'alert-info',    Icon: Info          },
    warning: { cls: 'alert-warning', Icon: AlertCircle   },
    danger:  { cls: 'alert-danger',  Icon: AlertCircle   },
    success: { cls: 'alert-success', Icon: CheckCircle2  },
  };
  const { cls, Icon } = map[type] || map.info;
  return (
    <div className={`alert ${cls}`}>
      <Icon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, fontSize: 13.5 }}>{children}</div>
      {action}
    </div>
  );
}

/* ── SkeletonRow ─────────────────────────────────────────────── */
export function SkeletonRows({ cols = 5, rows = 6 }) {
  return [...Array(rows)].map((_, i) => (
    <tr key={i}>
      {[...Array(cols)].map((_, j) => (
        <td key={j}><div className="skeleton" style={{ height: 16, width: j === 0 ? 150 : 80 }} /></td>
      ))}
    </tr>
  ));
}

/* ── PageHeader ──────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-sub">{subtitle}</p>}
      </div>
      {children && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>}
    </div>
  );
}
